import React from "react";
import styled from "styled-components/macro";
import { IconButton } from "@mui/material";
import { usePrevious, usePreviousDistinct } from "react-use";
import { devicePaddings } from "../../../tools/device";
import { stripGens } from "../../../sharedJs__generated/gens";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import { removeDiacritics } from "../../../sharedJs__generated/functions";
import { useSeeHint } from "../../../components/hint/Hint";
import CardNewButton from "../card/CardNewButton";
import CardSettings from "../components/CardSettings";
import CardUndoButton from "../card/CardUndoButton";
import { useViewerQuery } from "../../../queries/viewerQuery";
import { CardValues, useGetState, useSetState } from "../../../components/ReduxProvider";
import { VOICEHINT } from "components/other/HintTooltip";
import { TUTORIALDONE } from "root/tutorial/steps/Verdict";
import { useKeyMy } from "hooks/useKeyMy";
import OpenAssistantButton from "../card/OpenAssistantButton";
import { faKeyboard, faMicrophone, faCircleNotch } from "@duocards/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useSpeechTapHold from "hooks/useSpeechTapHold";
import PronounceFeedback from "components/speech/PronounceFeedback";
import PronounceHint from "components/speech/PronounceHint";

const { bottom } = devicePaddings();

const MIC_TRANSITION_TIME = 600;
const MIC_REMIND_TIME = 3 * 24 * 60 * 60 * 1000; // 3 days

const Wrap = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${bottom + 5}px;
  margin: 0 auto;
  max-width: 640px;
  padding-bottom: 10px;
  z-index: 630;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const White = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  background: #ffffffee;
  z-index: 620;
`;

const Transcript = styled.input<{ correct?: boolean }>`
  position: absolute;
  top: -63px;
  width: calc(100% - 100px);
  color: ${({ theme, correct }) =>
    correct ? theme.duo.color.green : correct === false ? theme.duo.color.red : theme.duo.color.primary};
  padding: 10px 0;
  text-align: center;
  font-size: 17px;
  border: none;
  pointer-events: none;
  background: none;
  &:focus {
    background: #fff;
  }
`;

const Icons = styled.div`
  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.duo.color.text};
  }
`;

const SIconButton = styled(IconButton)<{
  $listening?: boolean;
  $primary?: boolean;
  $highlight?: boolean;
}>`
  background: ${({ $primary, theme, $highlight }) =>
    $primary ? theme.duo.color.primary : $highlight ? "#fff" : "#fff5"} !important;
  color: ${({ $primary, $highlight }) => ($primary ? "white" : $highlight ? "#555" : "#aaa")};
  transform: ${({ $primary, $highlight }) => ($primary ? "scale(1.2)" : $highlight ? "scale(1)" : "scale(0.94)")};
  > svg {
    color: ${({ $listening, $primary }) => ($primary && $listening ? "white" : "inherit")};
  }
  transition: all 0.3s;
  border: ${({ theme, $highlight }) => theme.duo.border.grey + (!$highlight ? "77" : "")};
  margin: 0 5px;
  width: 52px;
  height: 52px;
  touch-action: none;
`;

const SSendIcon = styled(SendIcon)`
  && {
    color: ${({ theme }) => theme.duo.color.primary};
  }
`;

const MicButtonWrapper = styled.div<{ dx: number; dy: number }>`
  display: inline-block;
  transform: ${({ dx, dy }) => `translate(${dx}px, ${dy}px)`};
  transition:
    transform ${MIC_TRANSITION_TIME}ms cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.4s;
  pointer-events: auto;
`;

type Props = {
  card: CardValues;
  lang: string;
  nativeLang: string;
  highlight: boolean;
  highlightKeyboard: boolean;
  onRecognition: (correct: boolean) => void;
  romanization: boolean;
};

const CardsBottom: React.FC<Props> = React.memo(
  ({ card, lang, nativeLang, onRecognition, highlight, highlightKeyboard, romanization }) => {
    const [correct, setCorrect] = React.useState<boolean>();
    const [typed, setTyped] = React.useState<string>();
    const [focused, setFocused] = React.useState(false);
    const [micPos, setMicPos] = React.useState({ dx: 0, dy: 0 });
    const [micAnimating, setMicAnimating] = React.useState(false);
    const seeHint = useSeeHint();
    const textRef = React.useRef<HTMLInputElement>(null);
    const micWrapperRef = React.useRef<HTMLDivElement>(null);

    const [transcript, setTranscript] = React.useState<string | undefined>();
    const { hintWasSeen } = useViewerQuery();
    const prevCard = usePreviousDistinct(card, (prev, next) => prev?.id === next?.id);
    const prevCardState = usePrevious(card);

    const micLastUsed = useGetState("micLastUsed");
    const setMicLastUsed = useSetState("micLastUsed");
    const [remindMic, setRemindMic] = React.useState(Date.now() - (micLastUsed || 0) > MIC_REMIND_TIME);

    const [pronunciationAudio, setPronunciationAudio] = React.useState<string | undefined>();
    const [pronounceLoading, setPronounceLoading] = React.useState(false);

    const {
      listening,
      transcribing,
      supported: canSpeak,
      startListening,
      stopListening,
      cancelListening,
      handlers
    } = useSpeechTapHold({
      onSpeechData: setPronunciationAudio,
      onStart: () => {
        seeHint(VOICEHINT);
        setTyped(undefined);
        setCorrect(undefined);
        setTranscript(undefined);
        setPronunciationAudio(undefined);
        setMicLastUsed(Date.now());
      },
      onStop: () => {
        remindMic && setRemindMic(false);
      },
      languages: [lang],
      stopAfterMs: 10000,
      transcription: false,
      forceMp3: true // openai audio model do not support webm, which is default in most browsers
    });

    const handlePronounceFeedback = React.useCallback(
      (feedback) => {
        const pronounceCorrect = (feedback?.score || 0) > 50;
        setCorrect(pronounceCorrect);
        onRecognition(pronounceCorrect);
      },
      [onRecognition]
    );

    React.useEffect(() => {
      if (prevCardState === card) return;
      if (listening) cancelListening();
      setTranscript(undefined);
      setTyped(undefined);
      setCorrect(undefined);
      setPronunciationAudio(undefined);
    }, [card, prevCardState, cancelListening, listening]);

    React.useEffect(() => {
      if (!listening && transcript && correct === undefined) {
        onRecognition(false);
        setCorrect(false);
      }
    }, [listening, transcript, correct, onRecognition]);

    const startTyping = React.useCallback(
      (e) => {
        e.preventDefault();
        seeHint(VOICEHINT);
        if (listening) cancelListening();
        setTranscript(undefined);
        setCorrect(undefined);
        if (typed === undefined) {
          setTimeout(() => setTyped(""));
        } else {
          textRef?.current?.select();
        }
      },
      [seeHint, listening, cancelListening, typed]
    );

    useKeyMy((e) => e.code === "KeyK", startTyping, undefined, [startTyping]);
    useKeyMy((e) => e.code === "KeyM", listening ? stopListening : startListening, undefined, [listening]);

    const handleTyped = () => {
      if (typed) {
        const tpd = removeDiacritics(typed || "")
          .toLowerCase()
          .trim();
        const front = removeDiacritics(card.front).toLowerCase().trim();
        const back = removeDiacritics(card.back).toLowerCase().trim();

        const newCorrect =
          front === tpd ||
          stripGens(front, lang) === stripGens(tpd, lang) ||
          (!card.flipped && (back === tpd || stripGens(back, nativeLang) === stripGens(tpd, nativeLang)));
        setCorrect(newCorrect);
        onRecognition(newCorrect);
      } else {
        setTyped(undefined);
      }
      setFocused(false);
    };

    React.useEffect(() => {
      const el = micWrapperRef.current;
      if (!el) return;
      const newMicPos = { dx: 0, dy: 0 };

      if (highlight && remindMic) {
        const rect = el.getBoundingClientRect();
        newMicPos.dx = window.innerWidth / 2 - (rect.left + rect.width / 2);
        newMicPos.dy = -150;
      }

      const micPosChanged = micPos.dx !== newMicPos.dx || micPos.dy !== newMicPos.dy;

      if (micPosChanged) {
        setMicPos(newMicPos);
        setMicAnimating(true);
        setTimeout(() => {
          setMicAnimating(false);
          if (newMicPos.dx === 0 && newMicPos.dy === 0) {
            setMicLastUsed(Date.now()); // show mic reminder only once, even if not used
            setRemindMic(false);
          }
        }, MIC_TRANSITION_TIME);
      }
    }, [highlight, micPos, setMicLastUsed, remindMic]);

    const inKeyboardMode = typed !== undefined && focused;

    return (
      <>
        {focused && <White />}
        <Wrap>
          <PronounceFeedback
            open={!!pronunciationAudio && !pronounceLoading}
            anchorEl={handlers.ref.current}
            pronunciationAudio={pronunciationAudio}
            text={card.front}
            lang={lang}
            onClose={() => {
              setPronunciationAudio(undefined);
            }}
            onFeedback={handlePronounceFeedback}
            onLoading={setPronounceLoading}
            onTryAgain={() => {
              setPronunciationAudio(undefined);
              startListening();
            }}
          />
          <PronounceHint
            open={!hintWasSeen(VOICEHINT) && hintWasSeen(TUTORIALDONE) && !micAnimating}
            anchorEl={handlers.ref.current}
            textToSay={card.front}
            onClose={() => {
              seeHint(VOICEHINT);
            }}
          />

          {typed !== undefined && (
            <Transcript
              correct={correct}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => {
                setTimeout(() => {
                  setFocused(false); // delayed to let send-click to be first
                  typed === "" && setTyped(undefined);
                  handleTyped();
                }, 100);
              }}
              style={{ pointerEvents: focused ? "auto" : "none" }}
              className="nofilter"
              autoFocus
              ref={textRef}
              dir="auto"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  textRef?.current?.blur();
                  handleTyped();
                }
                e.stopPropagation();
              }}
            />
          )}
          <Icons>
            {!inKeyboardMode ? (
              <SIconButton title="k" $highlight={highlight || highlightKeyboard} onClick={startTyping}>
                <FontAwesomeIcon icon={faKeyboard} />
              </SIconButton>
            ) : (
              <SIconButton onClick={handleTyped}>
                <SSendIcon />
              </SIconButton>
            )}

            {inKeyboardMode ? (
              <SIconButton onClick={() => setTyped(undefined)}>
                <CloseIcon />
              </SIconButton>
            ) : null}

            {canSpeak && !inKeyboardMode && (
              <MicButtonWrapper ref={micWrapperRef} dx={micPos.dx} dy={micPos.dy}>
                <SIconButton
                  {...handlers}
                  title="m"
                  $highlight={highlight || !!pronunciationAudio || pronounceLoading}
                  $primary={listening || pronounceLoading}
                  $listening={listening}
                >
                  {transcribing || pronounceLoading ? (
                    <FontAwesomeIcon icon={faCircleNotch} spin />
                  ) : (
                    <FontAwesomeIcon icon={faMicrophone} />
                  )}
                </SIconButton>
              </MicButtonWrapper>
            )}
          </Icons>

          {hintWasSeen(TUTORIALDONE) && !inKeyboardMode && (
            <Icons>
              <OpenAssistantButton card={card} />
              <CardNewButton />
              <CardSettings lang={lang} romanization={romanization} />
              <CardUndoButton prevCard={prevCard as any} />
            </Icons>
          )}
        </Wrap>
      </>
    );
  }
);

export default CardsBottom;
